import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Loader,
  List,
  Rotate3d,
  RotateCcw,
  Scan,
  SlidersHorizontal,
  ZoomIn,
  ZoomOut,
  CircleDot,
  ChevronUp,
} from 'lucide-react';
import { PdfRenderer } from '../PdfRenderer';
import { DocxRenderer } from '../DocxRenderer';
import { NonPdfFallback } from '../NonPdfFallback';
import { Tooltip } from '../Tooltip';
import { useDocumentProgress } from '../../contexts/DocumentProgressContext';
import type { DocumentViewRequest } from '../../types/citation';
import { documentApi } from '../../services/api';
import { matchCitationText } from '../../utils/matchCitationText';
import { tokenStorage } from '../../utils/tokenStorage';
import { getFileExtension } from '../../utils/fileExtension';
import { processingApi } from '../../services/processingApi';
import { PageBookmarkButton } from '../sidebar/documents/PageBookmarkButton';
import { PageSummaryPanel } from '../sidebar/documents/PageSummaryPanel';
import { PageOutline } from '../sidebar/documents/PageOutline';
import { pdfCache } from '../../services/pdfCacheService';
import { useAskSidebar } from '../../contexts/AskSidebarContext';
interface CssFilters {
  brightness: number;
  contrast: number;
  grayscale: boolean;
  invert: boolean;
}

/** Transform a normalised bounding box to account for page rotation. */
function rotateHighlightBox(
  box: { x: number; y: number; width: number; height: number },
  rotation: number,
): { x: number; y: number; width: number; height: number } {
  switch (((rotation % 360) + 360) % 360) {
    case 90:
      return { x: 1 - box.y - box.height, y: box.x, width: box.height, height: box.width };
    case 180:
      return { x: 1 - box.x - box.width, y: 1 - box.y - box.height, width: box.width, height: box.height };
    case 270:
      return { x: box.y, y: 1 - box.x - box.width, width: box.height, height: box.width };
    default:
      return box;
  }
}

const PDF_EXTENSIONS = new Set(['pdf']);
const DOCX_EXTENSIONS = new Set(['docx']);
/** Non-PDF document types converted to PDF server-side via ?preview=true */
const CONVERTED_PDF_EXTENSIONS = new Set(['doc', 'rtf', 'odt', 'txt']);

const DOCX_CONTENT_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
const CONVERTED_PDF_CONTENT_TYPES = new Set([
  'application/msword',
  'application/rtf',
  'text/rtf',
  'application/vnd.oasis.opendocument.text',
  'text/plain',
]);

function getDocumentUrl(documentId: string): string {
  const base = import.meta.env.VITE_API_BASE_URL || '/api';
  return `${base}/documents/${documentId}/content`;
}

interface DocumentPanelProps {
  request: DocumentViewRequest;
  onClose: () => void;
  allDocuments?: Array<{ documentId: string; filename: string }>;
  onSwitchDocument?: (documentId: string) => void;
  caseId?: string;
  onPageChange?: (page: number) => void;
  onTotalPagesChange?: (total: number) => void;
}

const GENERIC_NAMES = new Set(['Document', 'Unknown document', '']);

export const DocumentPanel: React.FC<DocumentPanelProps> = ({ request, onClose, caseId, onPageChange, onTotalPagesChange }) => {
  const { markPageVisited } = useDocumentProgress();
  const { requestSidebarWidth } = useAskSidebar();
  const [autoWideToast, setAutoWideToast] = useState(false);

  // ── Citation navigation toast ─────────────────────────────────────────────
  const [navToast, setNavToast] = useState<{ documentName: string; pageNumber?: number } | null>(null);
  const prevRequestRef = useRef<string | null>(null);

  useEffect(() => {
    const key = `${request.documentId}:${request.pageNumber}`;
    if (prevRequestRef.current !== null && prevRequestRef.current !== key) {
      setNavToast({
        documentName: request.documentName || 'Document',
        pageNumber: request.pageNumber,
      });
      const timer = setTimeout(() => setNavToast(null), 3500);
      return () => clearTimeout(timer);
    }
    prevRequestRef.current = key;
  }, [request.documentId, request.pageNumber, request.documentName]);

  const [correctedPage, setCorrectedPage] = useState<number | null>(null);
  const citationPage = correctedPage ?? (request.pageNumber || 1);
  // navPage = the page PdfRenderer should scroll to (set by citation click or nav buttons).
  // currentPage = the page PdfRenderer reports as topmost (set by observer).
  // These are intentionally separate to prevent a feedback loop where the
  // observer reports a transient page during scroll → echoed back as nav target.
  const [navPage, setNavPage] = useState(citationPage);
  const [currentPage, setCurrentPage] = useState(citationPage);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [resolvedName, setResolvedName] = useState(request.documentName);
  const [highlightFound, setHighlightFound] = useState(true);
  const [highlightScore, setHighlightScore] = useState<number>(1.0);
  const [highlightBox, setHighlightBox] = useState<{ x: number; y: number; width: number; height: number } | undefined>();
  const [geometryMatched, setGeometryMatched] = useState(false);
  const [pageGeometry, setPageGeometry] = useState<Array<{ text: string; left: number; top: number; width: number; height: number }> | null>(null);

  const [outlineOpen, setOutlineOpen] = useState(false);

  // ── Page rotation (manual per-page) ──────────────────────────────────────
  const [pageRotations, setPageRotations] = useState<Record<number, number>>({});

  const currentPageRotation = pageRotations[currentPage] ?? 0;

  const handleRotate = useCallback(() => {
    setPageRotations(prev => {
      const current = prev[currentPage] ?? 0;
      const next = (current + 270) % 360; // counter-clockwise
      return { ...prev, [currentPage]: next };
    });
  }, [currentPage]);

  // ── Auto-rotate detection ──────────────────────────────────────────────
  // Default ON — detection is based on per-page text-transform analysis and is
  // reliable enough that users don't need to toggle it per document. The
  // preference is persisted so that if a user explicitly turns it off, that
  // choice sticks across sessions.
  const [autoRotateEnabled, setAutoRotateEnabled] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('clinlexis.autoRotate');
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try { localStorage.setItem('clinlexis.autoRotate', String(autoRotateEnabled)); } catch { /* ignore */ }
  }, [autoRotateEnabled]);
  const [detectedRotations, setDetectedRotations] = useState<Record<number, number>>({});
  const hasDetectedPages = Object.keys(detectedRotations).length > 0;

  const handleAutoRotateDetected = useCallback((rotations: Record<number, number>) => {
    setDetectedRotations((prev) => ({ ...prev, ...rotations }));
  }, []);

  // Apply or remove auto-rotations when the toggle changes
  useEffect(() => {
    if (autoRotateEnabled) {
      setPageRotations(prev => {
        const merged = { ...prev };
        for (const [page, rotation] of Object.entries(detectedRotations)) {
          // Only apply if the user hasn't manually rotated this page
          if (!(Number(page) in prev)) {
            merged[Number(page)] = rotation;
          }
        }
        return merged;
      });
    } else {
      setPageRotations(prev => {
        const cleaned = { ...prev };
        for (const page of Object.keys(detectedRotations)) {
          // Only remove if it matches the auto-detected value (user didn't override)
          if (cleaned[Number(page)] === detectedRotations[Number(page)]) {
            delete cleaned[Number(page)];
          }
        }
        return cleaned;
      });
    }
  }, [autoRotateEnabled, detectedRotations]);

  const [resolvedContentType, setResolvedContentType] = useState<string | null>(null);
  const [resolvingType, setResolvingType] = useState(false);
  const [docxPreviewFailed, setDocxPreviewFailed] = useState(false);

  // PDF cache integration — lookup cached data and pin/unpin on mount/unmount
  const [cachedPdfData, setCachedPdfData] = useState<ArrayBuffer | undefined>(() => {
    return pdfCache.get(request.documentId) ?? undefined;
  });

  useEffect(() => {
    const cached = pdfCache.get(request.documentId);
    setCachedPdfData(cached ?? undefined);
    if (cached) pdfCache.pin(request.documentId);
    return () => { pdfCache.unpin(request.documentId); };
  }, [request.documentId]);


  // True if observer confirms we're on the citation page OR we've navigated
  // there (navPage). Also true when page correction redirected us to the
  // actual page where the text was found. The observer can transiently report
  // the wrong page during scroll transitions, so trusting navPage prevents
  // search text from being yanked away mid-scroll.
  const correctedTarget = correctedPage ?? citationPage;
  const isOnCitationPage = currentPage === citationPage || navPage === citationPage
    || currentPage === correctedTarget || navPage === correctedTarget;

  // Skip text-layer search when Textract geometry already matched — Textract
  // coordinates are more reliable for scanned/rotated documents.
  const activeSearchText = isOnCitationPage && !geometryMatched ? request.searchText : undefined;

  const ext = getFileExtension(resolvedName);

  // Detect document type from extension OR content-type (fallback for extensionless names)
  const isPdf = PDF_EXTENSIONS.has(ext);
  const isDocx = DOCX_EXTENSIONS.has(ext) || (resolvedContentType != null && DOCX_CONTENT_TYPES.has(resolvedContentType));
  const isConvertedPdf = CONVERTED_PDF_EXTENSIONS.has(ext) || (resolvedContentType != null && CONVERTED_PDF_CONTENT_TYPES.has(resolvedContentType));

  // When the extension is unrecognised (e.g. label-only names like "Gp Records"),
  // default to PDF while we wait for the content-type resolution to complete.
  const hasKnownExtension = isPdf || isDocx || isConvertedPdf;
  // DOCX/DOC/RTF/ODT/TXT files use PdfRenderer via ?preview=true when a converted PDF exists.
  // If loading fails (no converted PDF yet), DOCX falls back to DocxRenderer,
  // other formats fall back to NonPdfFallback.
  const usePdfRenderer = isPdf || (isConvertedPdf && !docxPreviewFailed) || (isDocx && !docxPreviewFailed) || !hasKnownExtension;

  // Reset state when document/citation changes
  useEffect(() => {
    setCorrectedPage(null);
    setNavPage(request.pageNumber || 1);
    setCurrentPage(request.pageNumber || 1);
    setTotalPages(0);
    setResolvedName(request.documentName);
    setResolvedContentType(null);
    setResolvingType(false);
    setHighlightFound(true);
    setHighlightScore(1.0);
    setHighlightBox(undefined);
    setPageGeometry(null);
    setGeometryMatched(false);
    setDocxPreviewFailed(false);
  }, [request.documentId, request.pageNumber, request.documentName, request.searchText]);

  // Reset per-page rotations and detections when the document changes
  useEffect(() => {
    setPageRotations({});
    setDetectedRotations({});
  }, [request.documentId]);

  // Fetch pre-computed page rotations from Tesseract OSD (run at OCR time on
  // the backend). This is the authoritative source for orientation corrections
  // on scanned pages; client-side text-matrix detection remains as a fallback
  // for pages where OSD produced nothing.
  useEffect(() => {
    if (!request.documentId) return;
    let cancelled = false;
    documentApi.getPageRotations(request.documentId)
      .then((rotations) => {
        if (cancelled || !rotations) return;
        setDetectedRotations((prev) => ({ ...prev, ...rotations }));
        // Auto-widen the sidebar when rotated pages are detected, only from compact
        const hasRotated = Object.values(rotations).some((r) => r !== 0);
        if (hasRotated) {
          const currentWidth = parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue('--sidebar-panel-width') || '0'
          );
          if (currentWidth <= 650) {
            requestSidebarWidth('max');
            setAutoWideToast(true);
            setTimeout(() => { if (!cancelled) setAutoWideToast(false); }, 3500);
          }
        }
      })
      .catch(() => { /* non-fatal — fall back to client-side detection */ });
    return () => { cancelled = true; };
  }, [request.documentId, requestSidebarWidth]);

  // Resolve the real filename and content-type when the name is generic or lacks a file extension
  useEffect(() => {
    const knownExt = getFileExtension(request.documentName);
    const hasKnownExtension = PDF_EXTENSIONS.has(knownExt) || DOCX_EXTENSIONS.has(knownExt) || CONVERTED_PDF_EXTENSIONS.has(knownExt);
    if (!request.documentId || (hasKnownExtension && !GENERIC_NAMES.has(request.documentName))) return;
    let cancelled = false;
    setResolvingType(true);
    documentApi.getDocument(request.documentId)
      .then((doc: any) => {
        if (cancelled) return;
        if (doc?.filename) setResolvedName(doc.filename);
        if (doc?.contentType) setResolvedContentType(doc.contentType);
      })
      .catch(() => { /* keep the fallback name */ })
      .finally(() => { if (!cancelled) setResolvingType(false); });
    return () => { cancelled = true; };
  }, [request.documentId, request.documentName]);

  // Nav buttons must update both navPage (drives PdfRenderer scroll) AND
  // currentPage (drives the toolbar indicator). Without the latter, the
  // indicator only moves once the observer reports the new topmost page,
  // which may never fire reliably for short scroll distances or when the
  // observer is suppressed/torn down across the transition.
  const goToPreviousPage = useCallback(() => {
    const next = Math.max(1, currentPage - 1);
    if (next === currentPage) return;
    setNavPage(next);
    setCurrentPage(next);
  }, [currentPage]);

  const goToNextPage = useCallback(() => {
    const next = Math.min(totalPages, currentPage + 1);
    if (next === currentPage) return;
    setNavPage(next);
    setCurrentPage(next);
  }, [currentPage, totalPages]);

  const zoomIn = useCallback(() => {
    setScale(s => Math.min(s + 0.25, 5.0));
  }, []);

  const zoomOut = useCallback(() => {
    setScale(s => Math.max(s - 0.25, 0.5));
  }, []);

  // Area-zoom state
  const [areaZoomed, setAreaZoomed] = useState(false);
  const [crosshairEnabled, setCrosshairEnabled] = useState(false);

  const handleAreaZoom = useCallback((newScale: number) => {
    setScale(newScale);
    setAreaZoomed(true);
  }, []);

  const resetAreaZoom = useCallback(() => {
    setScale(1.0);
    setAreaZoomed(false);
  }, []);

  // ── Image enhancement state ──────────────────────────────────────────────
  const [enhanceOpen, setEnhanceOpen] = useState(false);
  const [cssFilters, setCssFilters] = useState<CssFilters>({
    brightness: 100,
    contrast: 100,
    grayscale: false,
    invert: false,
  });
  const enhancementActive =
    cssFilters.brightness !== 100 ||
    cssFilters.contrast !== 100 ||
    cssFilters.grayscale ||
    cssFilters.invert;

  const cssFilterStyle = [
    cssFilters.brightness !== 100 && `brightness(${cssFilters.brightness}%)`,
    cssFilters.contrast !== 100 && `contrast(${cssFilters.contrast}%)`,
    cssFilters.grayscale && 'grayscale(100%)',
    cssFilters.invert && 'invert(100%)',
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  const resetEnhancement = useCallback(() => {
    setCssFilters({ brightness: 100, contrast: 100, grayscale: false, invert: false });
  }, []);

  const handleDownload = useCallback(async () => {
    try {
      const token = tokenStorage.getToken();
      const response = await fetch(getDocumentUrl(request.documentId), {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = resolvedName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch {
      // Silent fail — download button is secondary
    }
  }, [request.documentId, request.documentName]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (e.key === 'ArrowLeft' && e.altKey) {
        e.preventDefault();
        goToPreviousPage();
      } else if (e.key === 'ArrowRight' && e.altKey) {
        e.preventDefault();
        goToNextPage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPreviousPage, goToNextPage]);

  // Notify parent of page/total changes (for page outline sync)
  useEffect(() => { onPageChange?.(currentPage); }, [currentPage, onPageChange]);
  useEffect(() => { onTotalPagesChange?.(totalPages); }, [totalPages, onTotalPagesChange]);

  // Track page views for reading progress
  useEffect(() => {
    if (caseId && request.documentId && currentPage > 0) {
      markPageVisited(caseId, request.documentId, currentPage);
    }
  }, [caseId, request.documentId, currentPage, markPageVisited]);

  // When PdfRenderer finds the citation text on an adjacent page, correct to that page.
  const handlePageCorrection = useCallback((correctPage: number) => {
    console.warn(`[DocumentPanel] Citation page corrected: ${citationPage} -> ${correctPage}`);
    setCorrectedPage(correctPage);
    setNavPage(correctPage);
    setCurrentPage(correctPage);
    setHighlightFound(true);
    setHighlightBox(undefined);
  }, [citationPage]);

  // Eagerly fetch stored line geometry on mount (parallel with PDF loading).
  // Textract geometry is the ground truth for scanned/OCR documents — it handles
  // rotated pages correctly, unlike the pdf.js text layer. So when geometry exists
  // and matches, we use it as the PRIMARY highlight mechanism and skip pdf.js text
  // layer matching entirely. For native PDFs (no geometry), we fall back to pdf.js.
  useEffect(() => {
    if (!request.documentId || !request.searchText) return;
    let cancelled = false;
    setGeometryMatched(false);

    const tryGeometryMatch = async () => {
      // Try the citation page first
      try {
        const lines = await documentApi.getLineGeometry(request.documentId, citationPage);
        if (cancelled) return;
        setPageGeometry(lines);

        if (lines && lines.length > 0) {
          const box = matchCitationText(lines, request.searchText!);
          if (box) {
            setHighlightBox(box);
            setHighlightFound(false);
            setHighlightScore(1.0);
            setGeometryMatched(true);
            return; // matched on target page
          }
        }
      } catch { /* continue to adjacent pages */ }

      // Target page failed — try adjacent pages (off-by-one is common)
      for (const offset of [1, -1]) {
        const adjPage = citationPage + offset;
        if (adjPage < 1 || cancelled) continue;
        try {
          const adjLines = await documentApi.getLineGeometry(request.documentId, adjPage);
          if (cancelled) return;
          if (adjLines && adjLines.length > 0) {
            const box = matchCitationText(adjLines, request.searchText!);
            if (box) {
              setPageGeometry(adjLines);
              setHighlightBox(box);
              setHighlightFound(false);
              setHighlightScore(1.0);
              setGeometryMatched(true);
              // Correct to the adjacent page
              setCorrectedPage(adjPage);
              setNavPage(adjPage);
              setCurrentPage(adjPage);
              return;
            }
          }
        } catch { /* continue */ }
      }
    };

    tryGeometryMatch();
    return () => { cancelled = true; };
  }, [request.documentId, request.searchText, citationPage]);

  // For native PDFs (no geometry), when text-layer search also fails, try geometry as last resort
  useEffect(() => {
    if (highlightFound || highlightBox || !request.searchText || !pageGeometry) return;
    const box = matchCitationText(pageGeometry, request.searchText);
    if (box) setHighlightBox(box);
  }, [highlightFound, highlightBox, request.searchText, pageGeometry]);

  // Report citation highlight failure when both mechanisms have been tried and failed
  const failureReportedRef = useRef<string | null>(null);
  useEffect(() => {
    if (highlightFound) return;
    if (highlightBox) return;
    if (!request.searchText || !request.documentId) return;

    const key = `${request.documentId}:${citationPage}:${request.searchText.substring(0, 30)}`;
    if (failureReportedRef.current === key) return;

    const timer = setTimeout(() => {
      failureReportedRef.current = key;
      console.warn(
        `[DocumentPanel] Citation highlight failed: documentId=${request.documentId}, ` +
        `page=${citationPage}, textLayerSearched=true, ` +
        `boundingBoxSearched=${pageGeometry !== null}, ` +
        `hasStoredGeometry=${pageGeometry !== null && pageGeometry.length > 0}, ` +
        `searchText="${request.searchText!.substring(0, 80)}..."`,
      );
      processingApi.reportCitationFailure({
        documentId: request.documentId,
        pageNumber: citationPage,
        searchText: request.searchText!,
        textLayerSearched: true,
        boundingBoxSearched: pageGeometry !== null,
        hasStoredGeometry: pageGeometry !== null && pageGeometry.length > 0,
      });
    }, 2000);

    return () => clearTimeout(timer);
  }, [highlightFound, highlightBox, request.searchText, request.documentId, citationPage, pageGeometry]);

  // ── Match indicator state ─────────────────────────────────────────────────
  const [matchDetailOpen, setMatchDetailOpen] = useState(false);

  const matchInfo = (() => {
    if (!request.searchText) return null;
    if (!isOnCitationPage) return null;

    let dotColour: string;
    let method: string;
    let score: number | null = null;

    if (geometryMatched) {
      dotColour = 'bg-emerald-500';
      method = 'OCR scan';
    } else if (highlightFound && highlightScore >= 0.8) {
      dotColour = 'bg-cyan-500';
      method = 'Text layer';
      score = highlightScore;
    } else if (highlightFound && highlightScore > 0) {
      dotColour = 'bg-amber-500';
      method = 'Approximate match';
      score = highlightScore;
    } else if (!highlightFound && highlightBox) {
      dotColour = 'bg-emerald-500';
      method = 'OCR scan (fallback)';
    } else if (!highlightFound) {
      dotColour = 'bg-red-400';
      method = 'No match found';
    } else {
      return null;
    }

    return { dotColour, method, score };
  })();

  return (
    <div className="h-full flex flex-col border-l border-medical-200 dark:border-medical-700 bg-white dark:bg-medical-800 shadow-xl animate-slide-in-right relative">
      {/* Auto-wide toast */}
      {autoWideToast && (
        <div className="absolute top-10 left-1/2 -translate-x-1/2 z-50 bg-medical-800 dark:bg-medical-700 text-white text-xs px-3 py-1.5 rounded-full shadow-lg animate-fade-in pointer-events-none opacity-90 transition-opacity duration-500 whitespace-nowrap">
          Widened — rotated pages detected
        </div>
      )}
      {/* Citation navigation toast */}
      {navToast && (
        <div className="absolute top-10 left-1/2 -translate-x-1/2 z-50 bg-medical-800 dark:bg-medical-700 text-white text-xs px-3 py-1.5 rounded-full shadow-lg animate-fade-in pointer-events-none opacity-90 transition-opacity duration-500 whitespace-nowrap max-w-[90%] truncate">
          Navigating to {navToast.documentName}{navToast.pageNumber ? `, page ${navToast.pageNumber}` : ''}
        </div>
      )}
      {/* Toolbar — single row */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-medical-200 dark:border-medical-700 bg-medical-50 dark:bg-medical-900">
        {/* Back + Filename */}
        <div className="relative group/tip min-w-0 mr-auto flex">
          <button
            onClick={onClose}
            className="flex items-center gap-0.5 min-w-0 group"
          >
            <ChevronLeft className="h-3.5 w-3.5 flex-shrink-0 text-primary-600 dark:text-primary-400" />
            <span className="text-xs font-semibold text-medical-900 dark:text-medical-100 truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
              {resolvedName}
            </span>
          </button>
          <div className="absolute left-0 top-full mt-1.5 z-50 pointer-events-none opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150">
            <div className="bg-gray-900 dark:bg-medical-700 text-white text-[11px] leading-tight rounded-md px-2.5 py-1.5 shadow-lg whitespace-nowrap">
              Back to documents
            </div>
          </div>
        </div>

        {/* Page nav */}
        {usePdfRenderer && totalPages > 0 && (
          <div className="flex items-center flex-shrink-0">
            <Tooltip text="Previous page" compact>
              <button
                onClick={goToPreviousPage}
                disabled={currentPage <= 1}
                className="p-0.5 rounded text-medical-500 hover:bg-medical-200 dark:text-medical-400 dark:hover:bg-medical-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
            <span className="text-[11px] text-medical-500 dark:text-medical-400 tabular-nums min-w-[50px] text-center">
              {currentPage}/{totalPages}
            </span>
            <Tooltip text="Next page" compact>
              <button
                onClick={goToNextPage}
                disabled={currentPage >= totalPages}
                className="p-0.5 rounded text-medical-500 hover:bg-medical-200 dark:text-medical-400 dark:hover:bg-medical-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </div>
        )}

        <div className="w-px h-3.5 bg-medical-200 dark:bg-medical-700 flex-shrink-0" />

        {/* Zoom */}
        {usePdfRenderer && (
          <div className="flex items-center flex-shrink-0">
            <Tooltip text="Zoom out" compact>
              <button
                onClick={zoomOut}
                disabled={scale <= 0.5}
                className="p-0.5 rounded text-medical-500 hover:bg-medical-200 dark:text-medical-400 dark:hover:bg-medical-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ZoomOut className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
            <span className="text-[10px] text-medical-500 dark:text-medical-400 tabular-nums w-8 text-center">
              {Math.round(scale * 100)}%
            </span>
            <Tooltip text="Zoom in" compact>
              <button
                onClick={zoomIn}
                disabled={scale >= 5.0}
                className="p-0.5 rounded text-medical-500 hover:bg-medical-200 dark:text-medical-400 dark:hover:bg-medical-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ZoomIn className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </div>
        )}

        {/* Area zoom */}
        {usePdfRenderer && (
          <Tooltip
            text={crosshairEnabled ? 'Disable area zoom' : 'Area zoom — drag to zoom into a region'}
            compact
          >
            <button
              onClick={() => setCrosshairEnabled(prev => !prev)}
              className={`p-1 rounded transition-colors flex-shrink-0 ${
                crosshairEnabled
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                  : 'text-medical-500 hover:bg-medical-200 dark:text-medical-400 dark:hover:bg-medical-700'
              }`}
            >
              <Scan className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        )}

        {/* Rotate */}
        {usePdfRenderer && (
          <Tooltip
            text={currentPageRotation !== 0 ? `Rotate page (currently ${currentPageRotation}°)` : 'Rotate page'}
            compact
          >
            <button
              onClick={handleRotate}
              className={`p-1 rounded transition-colors flex-shrink-0 ${
                currentPageRotation !== 0
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                  : 'text-medical-500 hover:bg-medical-200 dark:text-medical-400 dark:hover:bg-medical-700'
              }`}
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        )}

        {/* Auto-rotate */}
        {usePdfRenderer && hasDetectedPages && (
          <Tooltip
            text={autoRotateEnabled
              ? `Auto-rotate on — ${Object.keys(detectedRotations).length} page(s) corrected`
              : `Auto-rotate — ${Object.keys(detectedRotations).length} page(s) detected with vertical text`}
            compact
          >
            <button
              onClick={() => setAutoRotateEnabled(prev => !prev)}
              className={`p-1 rounded transition-colors flex-shrink-0 ${
                autoRotateEnabled
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                  : 'text-medical-500 hover:bg-medical-200 dark:text-medical-400 dark:hover:bg-medical-700'
              }`}
            >
              <Rotate3d className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        )}

        {/* Invert (quick toggle) */}
        {usePdfRenderer && (
          <Tooltip text={cssFilters.invert ? 'Invert off' : 'Invert colours'} compact>
            <button
              onClick={() => setCssFilters(f => ({ ...f, invert: !f.invert }))}
              className={`p-1 rounded transition-colors flex-shrink-0 ${
                cssFilters.invert
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                  : 'text-medical-500 hover:bg-medical-200 dark:text-medical-400 dark:hover:bg-medical-700'
              }`}
            >
              <CircleDot className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        )}

        {/* More enhancement options */}
        {usePdfRenderer && (
          <Tooltip text={enhanceOpen ? 'Hide enhancement options' : 'More enhancement options'} compact>
            <button
              onClick={() => setEnhanceOpen(v => !v)}
              className={`p-1 rounded transition-colors flex-shrink-0 ${
                enhanceOpen || (enhancementActive && !cssFilters.invert)
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                  : 'text-medical-500 hover:bg-medical-200 dark:text-medical-400 dark:hover:bg-medical-700'
              }`}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        )}

        {/* Outline */}
        {usePdfRenderer && totalPages > 0 && (
          <Tooltip text={outlineOpen ? 'Hide page outline' : 'Show page outline'} compact>
            <button
              onClick={() => setOutlineOpen(v => !v)}
              className={`p-1 rounded transition-colors flex-shrink-0 ${
                outlineOpen
                  ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30'
                  : 'text-medical-500 hover:bg-medical-200 dark:text-medical-400 dark:hover:bg-medical-700'
              }`}
            >
              <List className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        )}

        {/* Bookmark */}
        {caseId && (
          <PageBookmarkButton
            documentId={request.documentId}
            caseId={caseId}
            pageNumber={currentPage}
          />
        )}

        {/* Download */}
        <Tooltip text="Download document" align="right" compact>
          <button
            onClick={handleDownload}
            className="p-1 rounded text-medical-500 hover:bg-medical-200 dark:text-medical-400 dark:hover:bg-medical-700 transition-colors flex-shrink-0"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
        </Tooltip>

      </div>

      {/* Enhancement panel */}
      {enhanceOpen && (
        <div className="border-b border-medical-200 dark:border-medical-700 bg-white dark:bg-medical-800 px-3 py-3 space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-medical-500 dark:text-medical-400 uppercase tracking-wide">Image Enhancement</span>
            <div className="flex items-center gap-2">
              {enhancementActive && (
                <button
                  onClick={resetEnhancement}
                  className="text-[10px] text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Reset all
                </button>
              )}
              <button
                onClick={() => setEnhanceOpen(false)}
                className="p-0.5 rounded text-medical-400 hover:text-medical-600 hover:bg-medical-100 dark:text-medical-500 dark:hover:text-medical-300 dark:hover:bg-medical-700 transition-colors"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <EnhanceSlider label="Brightness" value={cssFilters.brightness} min={50} max={200} unit="%" onChange={v => setCssFilters(f => ({ ...f, brightness: v }))} />
          <EnhanceSlider label="Contrast" value={cssFilters.contrast} min={50} max={300} unit="%" onChange={v => setCssFilters(f => ({ ...f, contrast: v }))} />
          <EnhanceToggle label="Grayscale" checked={cssFilters.grayscale} onChange={v => setCssFilters(f => ({ ...f, grayscale: v }))} />
          <EnhanceToggle label="Invert" subtitle="White on black" checked={cssFilters.invert} onChange={v => setCssFilters(f => ({ ...f, invert: v }))} />
        </div>
      )}

      {/* Document content + optional page outline */}
      <div className="flex-1 flex min-h-0">
        {/* Document renderer */}
        <div className="flex-1 flex flex-col relative min-h-0 min-w-0">
          <div className="flex-1 relative min-h-0">
            {/* Floating match indicator */}
            {matchInfo && (
              <div className="absolute top-2 right-2 z-20">
                <button
                  onClick={() => setMatchDetailOpen(v => !v)}
                  className="group flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/90 dark:bg-medical-800/90 shadow-sm border border-medical-200 dark:border-medical-700 backdrop-blur-sm hover:shadow-md transition-all"
                >
                  <span className={`inline-block w-2 h-2 rounded-full ${matchInfo.dotColour} flex-shrink-0`} />
                  <span className="text-[10px] text-medical-500 dark:text-medical-400 hidden group-hover:inline">
                    {matchInfo.method}
                  </span>
                </button>
                {matchDetailOpen && (
                  <div className="absolute top-full right-0 mt-1 w-72 bg-white dark:bg-medical-800 rounded-lg shadow-lg border border-medical-200 dark:border-medical-700 p-3 animate-fade-in">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`inline-block w-2.5 h-2.5 rounded-full ${matchInfo.dotColour}`} />
                      <span className="text-xs font-medium text-medical-700 dark:text-medical-300">
                        {matchInfo.method}
                        {matchInfo.score != null && ` · ${Math.round(matchInfo.score * 100)}%`}
                        {totalPages > 0 && ` · Page ${correctedTarget} of ${totalPages}`}
                      </span>
                    </div>
                    {request.searchText && (
                      <div className="text-[11px] text-medical-600 dark:text-medical-400 leading-relaxed bg-medical-50 dark:bg-medical-900/50 rounded p-2 max-h-48 overflow-y-auto">
                        "{request.searchText}"
                      </div>
                    )}
                    <p className="text-[10px] text-medical-400 dark:text-medical-500 mt-1.5">
                      {matchInfo.method.includes('OCR')
                        ? 'Located via optical character recognition of the scanned document.'
                        : matchInfo.method === 'No match found'
                          ? 'The cited text could not be located on this page.'
                          : 'Located by searching the document\u2019s embedded text layer.'}
                    </p>
                  </div>
                )}
              </div>
            )}
            {resolvingType ? (
              <div className="flex items-center justify-center h-full min-h-[200px]">
                <Loader className="h-8 w-8 animate-spin text-primary-600" />
                <span className="ml-2 text-medical-600 dark:text-medical-400">Loading document...</span>
              </div>
            ) : usePdfRenderer ? (
              <PdfRenderer
                documentUrl={(isConvertedPdf || isDocx) ? `${getDocumentUrl(request.documentId)}?preview=true` : getDocumentUrl(request.documentId)}
                documentId={request.documentId}
                page={navPage}
                scale={scale}
                searchText={activeSearchText}
                onLoadComplete={setTotalPages}
                onCurrentPageChange={setCurrentPage}
                onHighlightResult={isOnCitationPage ? (found: boolean, score?: number) => {
                  setHighlightFound(found);
                  setHighlightScore(score ?? (found ? 1.0 : 0));
                } : undefined}
                onPageCorrection={isOnCitationPage ? handlePageCorrection : undefined}
                onError={(isDocx || isConvertedPdf) ? (errMsg: string) => { console.warn('[DocumentPanel] PDF preview failed, falling back:', errMsg); setDocxPreviewFailed(true); setHighlightFound(false); setHighlightScore(0); } : undefined}
                highlightBox={isOnCitationPage && highlightBox
                  ? rotateHighlightBox(highlightBox, pageRotations[navPage] ?? 0)
                  : undefined}
                cachedData={cachedPdfData}
                pageRotations={Object.keys(pageRotations).length > 0 ? pageRotations : undefined}
                areaZoomActive={crosshairEnabled}
                onAreaZoom={handleAreaZoom}
                onAreaZoomReset={areaZoomed ? resetAreaZoom : undefined}
                onAutoRotateDetected={handleAutoRotateDetected}
                cssFilterStyle={cssFilterStyle}

                className="h-full"
              />
            ) : isDocx ? (
              <DocxRenderer
                documentUrl={getDocumentUrl(request.documentId)}
                searchText={activeSearchText}
                className="h-full"
                onHighlightResult={isOnCitationPage ? (found: boolean, score?: number) => {
                  setHighlightFound(found);
                  setHighlightScore(score ?? (found ? 1.0 : 0));
                } : undefined}
                onLoadComplete={() => setTotalPages(1)}
              />
            ) : (
              <NonPdfFallback
                documentUrl={getDocumentUrl(request.documentId)}
                documentName={resolvedName}
                pageNumber={request.pageNumber}
                className="h-full"
              />
            )}
          </div>

          {/* Floating page number */}
          {usePdfRenderer && totalPages > 0 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 bg-black/60 text-white text-[11px] tabular-nums px-2.5 py-1 rounded-full pointer-events-none backdrop-blur-sm shadow-sm">
              Page {currentPage} of {totalPages}
            </div>
          )}

          {/* Per-page summary panel */}
          <PageSummaryPanel
            documentId={request.documentId}
            pageNumber={currentPage}
            caseId={caseId}
          />
        </div>

        {/* Page outline TOC */}
        {outlineOpen && totalPages > 0 && (
          <div className="w-[220px] flex-shrink-0 border-l border-medical-200 dark:border-medical-700 bg-white dark:bg-medical-800 overflow-y-auto">
            <PageOutline
              documentId={request.documentId}
              currentPage={currentPage}
              totalPages={totalPages}
              onNavigateToPage={(p) => { setNavPage(p); setCurrentPage(p); }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ── Enhancement controls (compact, for sidebar panel) ─────────────────────

const EnhanceSlider: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  onChange: (v: number) => void;
}> = ({ label, value, min, max, step = 1, unit = '', onChange }) => (
  <div className="flex items-center gap-2">
    <label className="text-[11px] text-medical-600 dark:text-medical-300 w-24 shrink-0">{label}</label>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={e => onChange(Number(e.target.value))}
      className="flex-1 h-1 accent-primary-600 cursor-pointer"
    />
    <span className="text-[10px] text-medical-400 dark:text-medical-500 w-10 text-right tabular-nums">
      {Number.isInteger(step) ? value : value.toFixed(1)}{unit}
    </span>
  </div>
);

const EnhanceToggle: React.FC<{
  label: string;
  subtitle?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, subtitle, checked, onChange }) => (
  <label className="flex items-start gap-2 cursor-pointer group">
    <input
      type="checkbox"
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      className="mt-0.5 h-3 w-3 rounded border-medical-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
    />
    <div>
      <span className="text-[11px] text-medical-600 dark:text-medical-300 group-hover:text-medical-900 dark:group-hover:text-medical-100">{label}</span>
      {subtitle && <p className="text-[9px] text-medical-400 dark:text-medical-500 leading-tight">{subtitle}</p>}
    </div>
  </label>
);
